<script lang="ts" setup>
import { useSchoolStore } from "@/store/school";
import { useWindowSize } from "@vueuse/core";
import { openEditSchoolInformationModal } from "@/components/modals/modalDefinitions";
import { useIsMobile } from "@/assets/js/helpers";
import { DateInstance } from "@/assets/js/dateHelper";
import dayjs from "dayjs";

definePage({
	name: "school.school-setup.public-page-management",
});

const schoolStore = useSchoolStore();
const { width } = useWindowSize();
const isMobile = useIsMobile();
const computedHeight = computed(() => (isMobile.value ? 130 : 220));

const fixedWebsiteUrl = computed(() => {
	const url = schoolStore.schoolData.scl_websiteurl?.trim() || "";
	if (!url) return "";
	if (url.startsWith("http://") || url.startsWith("https://")) return url;
	return "https://" + url;
});

const displayWebsiteUrl = computed(() => {
	const rawUrl = schoolStore.schoolData.scl_websiteurl || "";
	return rawUrl.replace(/^https?:\/\//, "");
});

const isSubscriptionExpiringSoon = computed(() => {
	const end = dayjs(schoolStore.schoolData.subscription.sbr_end);
	const diffInMinutes = end.diff(dayjs(), "minute");
	const threeWeeksInMinutes = 3 * 7 * 24 * 60;

	return diffInMinutes > 0 && diffInMinutes < threeWeeksInMinutes;
});
</script>

<template>
	<div
		v-if="schoolStore.schoolData"
		id="public-page-management"
	>
		<div class="top-wrapper">
			<div class="avatar-wrapper">
				<profile-image
					:name="schoolStore.schoolData.scl_name"
					:image="schoolStore.schoolData.scl_logo"
					:height="computedHeight"
				/>
			</div>
			<div class="basic-info-wrapper">
				<div class="basic-info-top-wrapper">
					<div class="title-wrapper">
						<span class="title">{{ schoolStore.schoolData.scl_name }}</span>
						<span
							v-if="
								schoolStore.schoolData.subscription &&
								schoolStore.schoolData.subscription.sbr_status === 'Active'
							"
							class="sub-valid"
							:class="{ 'sub-expiring-soon': isSubscriptionExpiringSoon }"
						>
							{{ $t("app.school.subscriptionValidThrough") }}:
							{{
								DateInstance.getFormattedDate(
									schoolStore.schoolData.subscription.sbr_end,
									"DD.MM.YYYY.",
									false,
								)
							}}
						</span>
					</div>
					<div class="edit-btn-wrapper">
						<p-button
							:pt="{
								root: {
									class: 'edit-button-root',
								},
							}"
							@click="openEditSchoolInformationModal(schoolStore.schoolData)"
						>
							<i-material-symbols-edit-outline />
							{{ $t("app.base.edit") }}
						</p-button>
					</div>
				</div>
				<div class="basic-info-bottom-wrapper">
					<school-highlighted-info
						v-if="width >= 1580"
						:school-data="schoolStore.schoolData"
					/>
				</div>
			</div>
		</div>
		<div class="bottom-wrapper">
			<school-highlighted-info
				v-if="width < 1580"
				:school-data="schoolStore.schoolData"
			/>
			<div class="bottom-inner-wrapper">
				<div class="bottom-top-wrapper">
					<div class="info-item-wrapper">
						<div class="icon-wrapper">
							<i-material-symbols-workspace-premium-outline-rounded />
						</div>
						<div class="content">
							<div class="label">
								{{ $t("app.school.accreditation") }}
							</div>
							<div class="text accreditation-text">
								<ul style="list-style: disc; padding-left: 1.5rem">
									<li
										v-for="item in schoolStore.schoolData.scl_accreditations"
										:key="item"
									>
										<span>{{ item }}</span>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
				<div class="bottom-bottom-wrapper">
					<div class="bottom-bottom-top-wrapper">
						<div class="contract-types-wrapper">
							<div class="info-item-wrapper">
								<div class="icon-wrapper">
									<i-material-symbols:contract-outline-rounded />
								</div>
								<div class="content">
									<div class="label">
										{{ $t("app.school.contractType") }}
									</div>
									<div
										v-if="schoolStore.schoolData.contract_types"
										class="text contract-types-text"
									>
										<span
											v-for="ctt in schoolStore.schoolData.contract_types"
											:key="ctt.ctt_code"
										>
											{{ ctt.ctt_name }}
										</span>
									</div>
								</div>
							</div>
						</div>
						<div class="languages-wrapper">
							<div class="info-item-wrapper">
								<div class="icon-wrapper">
									<i-material-symbols:alarm-outline-rounded />
								</div>
								<div class="content">
									<div class="label">
										{{ $t("app.school.hourlyRate") }}
									</div>
									<div class="text">
										{{ schoolStore.schoolData.scl_hourlyrate }}
										<span v-if="schoolStore.schoolData.scl_hourlyrate">€</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="bottom-bottom-bottom-wrapper">
						<div class="info-item-wrapper">
							<div class="icon-wrapper">
								<i-material-symbols-contact-page-outline />
							</div>
							<div class="content">
								<div class="label">
									{{ $t("app.school.contactInformation") }}
								</div>
								<div class="text contact-information-text">
									<span
										v-if="schoolStore.schoolData.scl_websiteurl"
										class="contact-label"
									>
										{{ $t("app.school.website") }}
									</span>
									<span
										v-if="schoolStore.schoolData.scl_websiteurl"
										class="contact-text website-underline"
									>
										<a
											:href="fixedWebsiteUrl"
											target="_blank"
											rel="noopener noreferrer"
										>
											{{ displayWebsiteUrl }}
										</a>
									</span>
									<span
										v-if="schoolStore.schoolData.scl_email"
										class="contact-label"
									>
										{{ $t("app.school.contactPerson") }}
									</span>
									<span
										v-if="schoolStore.schoolData.scl_email"
										class="contact-text"
									>
										{{ schoolStore.schoolData.scl_email }}
									</span>
									<span
										v-if="schoolStore.schoolData.scl_phone"
										class="contact-label"
									>
										{{ $t("app.school.phoneNumber") }}
									</span>
									<span
										v-if="schoolStore.schoolData.scl_phone"
										class="contact-text"
									>
										{{ schoolStore.schoolData.scl_phone }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
#public-page-management {
	padding: 20px 16px 56px;
	max-height: calc(100vh - $nav-top-bar - 1px);
	overflow-y: auto;
	box-sizing: border-box;
	width: 100vw;
	overflow-x: hidden;

	.top-wrapper {
		display: flex;
		gap: 20px;
		margin-bottom: 20px;

		.avatar-wrapper {
			.avatar-root {
				height: 130px;
				width: 130px;
				border: 1px solid #333255;
				background-color: var(--p-primary-600);

				:deep() {
					.avatar-label {
						font-size: 80px;
						line-height: 80px;
					}
				}
			}
		}

		.basic-info-wrapper {
			flex: 1;
			box-sizing: border-box;
			max-width: calc(100% - 130px - 20px);

			.basic-info-top-wrapper {
				display: flex;
				flex-wrap: wrap;
				justify-content: flex-end;
				align-items: center;
				gap: 16px;
				box-sizing: border-box;
				max-width: 100%;

				.title-wrapper {
					max-width: 100%;
					box-sizing: border-box;
					display: flex;
					flex-direction: column;

					.title {
						font-size: 20px;
						font-weight: 700;
						font-family: Archivo, sans-serif;
						color: rgb(51 50 85);
						box-sizing: border-box;
						user-select: text;
						max-width: 100%;
						word-wrap: break-word;
					}

					.sub-valid {
						font-size: 11px;
						font-family: Archivo, sans-serif;
						color: $scrollbar-color-thumb;
						font-weight: 500;

						&.sub-expiring-soon {
							color: $danger;
						}
					}
				}

				.edit-btn-wrapper {
					display: flex;
					margin-left: auto;

					.edit-button-root {
						align-items: center;
						height: 45px;
						font-size: 20px;
						font-weight: 600;
						font-family: Archivo, sans-serif;
						align-self: end;

						.faico {
							height: 25px;
							width: 25px;
							color: $daidai-light-blue;
						}
					}
				}
			}
		}
	}

	.bottom-wrapper {
		width: 100%;

		.bottom-inner-wrapper {
			// display: flex;
			// flex-direction: column-reverse;
			width: 100%;

			.bottom-top-wrapper {
				width: 100%;

				.info-item-wrapper {
					width: 100%;

					.content {
						width: calc(100% - 34px);

						.label {
							font-family: Archivo, sans-serif;
							color: var(--p-primary-700);
						}

						.text {
							width: 100%;
							font-weight: 600;
							font-family: Archivo, sans-serif;
							color: $daidai-dark-blue;

							&.accreditation-text {
								width: 100%;
								display: flex;
								flex-direction: column;
								gap: 10px;
								word-wrap: break-word;

								ul {
									width: 100%;
									li {
										width: 100%;

										span {
											width: 100%;
											word-wrap: break-word;
										}
									}
								}
							}
						}
					}
				}
			}

			.bottom-bottom-wrapper {
				width: 100%;

				.bottom-bottom-bottom-wrapper {
					width: 100%;

					.info-item-wrapper {
						width: 100%;

						.content {
							width: calc(100% - 34px);

							.label {
								font-family: Archivo, sans-serif;
								color: var(--p-primary-700);
							}

							.text {
								width: 100%;
								display: flex;
								flex-direction: column;
								gap: 10px;
								font-weight: 600;
								font-family: Archivo, sans-serif;
								color: $daidai-dark-blue;

								&.contact-information-text {
									display: flex;
									flex-direction: column;
									gap: 5px;
									width: 100%;
									word-wrap: break-word;

									.contact-label {
										font-weight: 400;
										font-family: Archivo, sans-serif;
										color: var(--p-primary-700);
										font-size: 14px;
									}

									.contact-text {
										line-height: 18px;
										max-width: 100%;
										word-wrap: break-word;
										margin-bottom: 10px;

										&.website-underline {
											&:hover {
												text-decoration: underline;
											}
										}
									}
								}
							}
						}
					}
				}

				.bottom-bottom-top-wrapper {
					.contract-types-wrapper,
					.languages-wrapper {
						.info-item-wrapper {
							.content {
								.label {
									font-family: Archivo, sans-serif;
									color: var(--p-primary-700);
								}

								.text {
									font-weight: 600;
									font-family: Archivo, sans-serif;
									color: $daidai-dark-blue;

									&.contract-types-text {
										display: flex;
										flex-direction: column;
										gap: 10px;

										span {
											line-height: 18px;
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}

	@media screen and (width >= 600px) {
		.bottom-wrapper {
			.bottom-inner-wrapper {
				width: 100%;
				display: flex;
				flex-direction: column-reverse;

				.bottom-top-wrapper {
					.info-item-wrapper {
						.content {
							.label {
								font-weight: 600;
								font-size: 16px;
							}

							.text {
								&.accreditation-text {
									ul {
										font-weight: 400;
										li {
											span {
												font-weight: 400;
											}
										}
									}
								}
							}
						}
					}
				}

				.bottom-bottom-wrapper {
					display: flex;
					width: 100%;

					.bottom-bottom-top-wrapper {
						width: 36%;

						.contract-types-wrapper,
						.languages-wrapper {
							.info-item-wrapper {
								.content {
									.label {
										font-weight: 600;
										font-size: 16px;
									}

									.text {
										font-weight: 400;
										font-size: 16px;

										// &.contract-types-text {
										// }
									}
								}
							}
						}
					}

					.bottom-bottom-bottom-wrapper {
						.info-item-wrapper {
							.content {
								/* stylelint-disable-next-line no-descending-specificity */
								.label {
									font-weight: 600;
									font-size: 16px;
								}
								.contact-information-text {
									.contact-label {
										font-size: 16px !important;
									}

									.contact-text {
										font-weight: 400;
										font-size: 16px;
									}
								}
							}
						}
					}
				}
			}
		}
	}

	@media screen and (width >= 768px) {
		padding: 38px 55px 25px 25px;
		max-width: calc(100vw - $mini-sidebar-width);
		width: 100%;

		.top-wrapper {
			gap: 69px;
			margin-bottom: 55px;

			.avatar-wrapper {
				.avatar-root {
					height: 220px;
					width: 220px;

					:deep() {
						.avatar-label {
							font-size: 120px;
							line-height: 120px;
						}
					}
				}
			}

			.basic-info-wrapper {
				max-width: calc(100% - 220px - 69px);

				.basic-info-top-wrapper {
					.title-wrapper {
						.title {
							font-size: 32px;
						}

						.sub-valid {
							font-size: 13px;
						}
					}
				}

				.basic-info-bottom-wrapper {
					max-width: 100%;
					margin-top: 55px;
				}
			}
		}

		.bottom-wrapper {
			.bottom-inner-wrapper {
				.bottom-top-wrapper {
					width: 50%;

					.info-item-wrapper {
						margin-bottom: 30px;
						.content {
							.label {
								font-size: 18px;
							}

							.text {
								font-size: 20px;

								&.accreditation-text {
									span {
										line-height: 22px;
									}
								}
							}
						}
					}
				}

				.bottom-bottom-wrapper {
					.bottom-bottom-bottom-wrapper {
						.info-item-wrapper {
							margin-bottom: 30px;
							.content {
								.label {
									font-size: 18px;
								}

								.text {
									font-size: 20px;

									&.contact-information-text {
										.contact-label,
										.contact-text {
											font-size: 18px;
											line-height: 22px;
										}
									}
								}
							}
						}
					}

					.bottom-bottom-top-wrapper {
						.contract-types-wrapper {
							.info-item-wrapper {
								margin-bottom: 30px;
								.content {
									.label {
										font-size: 18px;
									}

									.text {
										font-size: 20px;

										&.contract-types-text {
											span {
												line-height: 22px;
											}
										}
									}
								}
							}
						}

						.languages-wrapper {
							.info-item-wrapper {
								margin-bottom: 30px;
								.content {
									.label {
										font-size: 18px;
									}

									.text {
										font-size: 20px;
									}
								}
							}
						}
					}
				}
			}
		}
	}

	@media screen and (width >= 1157px) {
		max-width: calc(100vw - $expanded-sidebar-width);

		.top-wrapper {
			.basic-info-wrapper {
				.basic-info-top-wrapper {
					.title-wrapper {
						.sub-valid {
							font-size: 15px;
						}
					}
				}
			}
		}
	}

	@media screen and (width >= 1580px) {
		.top-wrapper {
			width: 100%;
		}

		.bottom-wrapper {
			.bottom-inner-wrapper {
				flex-direction: row-reverse;
				justify-content: left;
				box-sizing: border-box;

				.bottom-top-wrapper {
					width: 40%;
				}

				.bottom-bottom-wrapper {
					display: flex;
					width: 60%;
					box-sizing: border-box;

					.bottom-bottom-top-wrapper {
						width: 39.27%;
					}

					.bottom-bottom-bottom-wrapper {
						width: 60%;
					}
				}
			}
		}
	}
}
</style>
